import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Zap, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR') : 'Jamais');

const Stat = ({ value, label, color = 'text-white', testId }) => (
  <div className="text-center p-2 bg-white/5 rounded">
    <p className={`text-2xl font-bebas ${color}`} data-testid={testId}>{value}</p>
    <p className="text-text-secondary text-xs">{label}</p>
  </div>
);

const SyncStats = ({ s }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
    <Stat value={s.movies_found} label="Dans le catalogue" testId="sync-found" />
    <Stat value={s.movies_updated} label="Ordre mis à jour" color="text-blue-400" testId="sync-updated" />
    <Stat value={s.movies_added} label="Ajoutés" color="text-green-400" testId="sync-added" />
    <Stat value={s.movies_removed} label="Retirés du tri" color="text-red-400" testId="sync-removed" />
  </div>
);

const progressOf = (s) => {
  if (s.phase === 'enrich' && s.new_total > 0) return Math.round((s.new_processed / s.new_total) * 100);
  return s.phase === 'fetch' ? 5 : 15;
};

export const CatalogueSyncCard = () => {
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/sync-status`);
      setStatus(data);
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [load]);

  const start = async () => {
    try {
      await axios.post(`${API}/admin/sync-catalogue`);
      load();
    } catch (err) {
      alert(err.response?.status === 409 ? 'Une synchronisation est déjà en cours' : 'Erreur lors du lancement');
    }
  };

  return (
    <Card className="bg-bg-secondary border-white/10 border-2 border-yellow-500/40" data-testid="catalogue-sync-card">
      <CardHeader>
        <CardTitle className="text-white font-work flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-400" />
          Synchronisation du catalogue Obiloz
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-text-secondary text-sm font-inter">
          Récupère tout le catalogue Obiloz dans l&apos;ordre exact de la section « Explorer le catalogue » (derniers ajouts en premier), met à jour l&apos;ordre et ajoute les nouveaux films avec TMDB.
        </p>
        {status?.running ? (
          <div className="p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg space-y-3" data-testid="sync-running">
            <div className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" />
              <span className="text-yellow-200 font-work font-semibold">Synchronisation en cours...</span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-3">
              <div className="bg-yellow-400 h-3 rounded-full transition-[width] duration-500" style={{ width: `${progressOf(status)}%` }} />
            </div>
            <SyncStats s={status} />
            <p className="text-yellow-200/80 text-xs font-inter" data-testid="sync-progress">{status.progress}</p>
          </div>
        ) : (
          <>
            <Button onClick={start} className="w-full bg-yellow-600 hover:bg-yellow-700 text-black font-work font-bold py-6 text-lg" data-testid="sync-catalogue-btn">
              <Zap className="w-5 h-5 mr-2" />
              Synchroniser le catalogue
            </Button>
            {status?.finished_at && (
              <div
                className={`p-3 rounded-lg text-sm space-y-2 border ${status.error ? 'bg-red-900/20 border-red-500/40' : 'bg-green-900/20 border-green-500/30'}`}
                data-testid="sync-last-result"
              >
                <p className={`${status.error ? 'text-red-200' : 'text-green-200'} font-work font-semibold`}>
                  {status.error ? 'Échec de la synchronisation' : 'Dernière synchronisation'} : {formatDate(status.finished_at)}
                </p>
                <SyncStats s={status} />
                {status.error && <p className="text-red-300 text-xs" data-testid="sync-error">Erreur : {status.error}</p>}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
